import { create } from "zustand";
import { persist } from "zustand/middleware";
import { API_URL, IndexDbStoreName, IndexedDbName } from "./const";
import { makeRequest } from "../util/AxiosInstance";
import { Notify } from "notiflix";
import { getMode, isIOS } from "../util/functions";
import { fileTypeFromBuffer } from "file-type";
import timezonesFallback from '../assets/timezones.json';
import { beamsClient } from "../components/PusherBeamClient";
import Notiflix from "notiflix";

const useProjectStore = create((set) => ({
  projects: [],
  ongoing: [],
  pending: [],
  completed: [],
  status: [],
  loadMore: async (type = 1, page = 1) => {
    /**
     * Type 1: Ongoing projects
     * Type 2: Pending projects
     * Type 3: Completed projects
     */
    try {
      if (typeof type !== 'number') {
        type = +type
      }
      makeRequest(`GET`, `${API_URL}/projects?status=${type}&page=${page}`)
        .then(async (response) => {
          if (!response.status) {
            throw new Error("Failed to fetch projects");
          }
          const data = response.data.data.data ?? [];
          let ongoing = useProjectStore.getState().ongoing;
          let pending = useProjectStore.getState().pending;
          let completed = useProjectStore.getState().completed;
          let projects = useProjectStore.getState().projects;

          switch (type) {
            case 1:
              set({ ongoing: [...ongoing, ...data] });
              break;
            case 2:
              set({ pending: [...pending, ...data] });
              break;
            case 3:
              set({ completed: [...completed, ...data] });
              break;
            default:
              set({
                projects: [...projects, ...data],
              });
          }
        }).catch((error) => {
          console.error("Error fetching projects:", error);
        })
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  },

  fetchProjects: (type = 1, page = 1) => {
    try {
      if (typeof type !== 'number') {
        type = +type
      }
      makeRequest(`GET`, `${API_URL}/projects?status=${type}&page=${page}`)
        .then(async (response) => {
          if (!response.status) {
            throw new Error("Failed to fetch projects");
          }
          const data = response.data.data.data ?? [];
          switch (type) {
            case 1:
              set({ ongoing: [...data] });
              break;
            case 2:
              set({ pending: [...data] });
              break;
            case 3:
              set({ completed: [...data] });
              break;
            default:
              set({
                projects: [...data],
              });
          }
        }).catch((error) => {
          console.error("Error fetching projects:", error);
        })
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  },
  // Function to add a new project
  addProject: (newProject) => {
    set((state) => ({
      projects: [...state.projects, newProject],
    }));
  },
  removeProject: (projectId) => {
    set((state) => ({
      projects: state.projects.filter((project) => project.id !== projectId),
    }));
  },

  loadStatus: () => {
    makeRequest('GET', `${API_URL}/get-project-all-status`)
      .then((response) => {
        let data = response.data.data
        set({ status: data });
      }).catch((error) => {
        console.error("Error fetching projects:", error);
      })
  }
}));

const userStore = create(persist((set) => ({
  user: null,
  loginUser: null,
  token: "",
  isLoading: false,
  channel: 'channel:doshby-realtime',
  setChannel: (channel) => set({ channel }),
  isLoggedIn: () => {
    try {
      const userStorage = localStorage.getItem("user-storage");
      if (userStorage) {
        const { state } = JSON.parse(userStorage);
        return !!state.token;
      }
      return false;
    } catch (e) {
      console.error("Failed to parse user-storage from localStorage", e);
      return false;
    }
  },
  setUser: (user) => {
    set({ user });
  },
  setLoginUser: (user) => {
    set({ user });
  },
  setToken: (token) => {
    set({ token });
  },
  getUser: async () => {
    set({ isLoading: true });
    makeRequest('GET', `${API_URL}/get-user`).then((response) => {
      set({ user: response.data.data });
    }).catch((error) => {
      Notify.failure("Failed to fetch user data");
      console.log(error);
    }).finally(() => {
      set({ isLoading: false });
    });
  },
  checkLogin: async () => {
    if (userStore.getState().isLoggedIn()) { // Use the corrected isLoggedIn check
      console.log("Token exists in local storage");
      userStore.getState().getUser();
    }
  },
  logout: () => {
    return new Promise((resolve) => {
      Notiflix.Block.circle('#root', 'Session expired, logging out...');
      const init = async () => {
        console.log('Logging out...');

        await useRoomStore.getState().clear();
        await navigationContext.getState().clear();
        await useFileRepoStore.getState().clear();
        await chatUserStore.getState().clear();
        if (!isIOS()) {
          beamsClient.clearAllState()
        }
        // Clear the entire persisted state for userStore
        localStorage.removeItem("user-storage");
        set({ token: "", user: null }); // Also clear in-memory state
        console.log('Done logging out redirecting')
        window.location.href = '/login';
        resolve(true);
      };
      init();
    });
  }
}),
  {
    name: "user-storage",
    getStorage: () => localStorage,
  }));

const useMeetingStore = create((set) => ({
  meetings: [],
  currentPage: 1,
  hasMore: true,
  isLoading: false,
  fetchMeetings: async (page) => {
    try {
      let userId = userStore.getState().user.id;
      set({ isLoading: true });
      makeRequest('GET', `/user-bookings/${userId}?page=${page}`).then((response) => {
        const data = response.data.data;
        set((state) => ({
          meetings: [...state.meetings, ...data],
          hasMore: response.data.next_page_url !== null,
        }));
      }).catch((error) => {
        console.error("Failed to fetch meetings", error);
      }).finally(() => {
        set({ isLoading: false });
      });
    } catch (error) {
      set({ isLoading: false });
      console.error("Failed to fetch meetings", error);
    }
  },
  loadMore: () => set((state) => ({ currentPage: state.currentPage + 1 })),
  setCurrentPage: (page) => set({ currentPage: page }),
}));

const navigationContext = create(persist((set) => ({
  ongoing: {
    page: 1,
  },
  completed: {
    page: 1,
  },
  pending: {
    page: 1,
  },
  setOngoingPage: (page) => set({ ongoing: { page } }),
  setCompletedPage: (page) => set({ completed: { page } }),
  setPendingPage: (page) => set({ pending: { page } }),
  clear: () => set({ ongoing: { page: 1 }, completed: { page: 1 }, pending: { page: 1 } }),
}), {
  name: "navigation-storage",
}))

const usePortfolioStore = create((set) => ({
  portfolios: [],
  setPortfolios: (portfolios) => set({ portfolios }),
  addPortfolio: (portfolio) => set((state) => ({ portfolios: [...state.portfolios, ...portfolio] })),
  currentPage: 1,
  totalItems: 0,
  totalPages: 0,
  hasMore: true,
  setCurrentPage: (page) => set({ currentPage: page }),
  increasePage: () => set((state) => ({ currentPage: state.currentPage + 1 })),
  setTotalItems: (totalItems) => set({ totalItems }),
  setTotalPages: (totalPages) => set({ totalPages }),
  setHasMore: (hasMore) => set({ hasMore }),
}))

const useProjectStatStore = create((set) => ({
  stats: {},
  setStats: (stats) => set({ stats }),
  fetchStats: async () => {
    makeRequest('GET', `${API_URL}/projects/stat`)
      .then((response) => {
        let dd = response.data.data
        set({ stats: dd });
      }).catch((error) => {
        console.error("Failed to fetch project status", error);
      });
  }
}))

const appStore = create((set) => ({
  isLoading: true,
  mode: getMode(),
  error: null,
  setMode: (mode) => set({ mode }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}));

const chatState = create((set) => ({
  roomId: '',
  show: false,
  chatId: null,
  unread: 0,
  roomInfo: null,
  openChatBox: false,
  searchDms: null,
  setSearchDms: (searchDms) => set({ searchDms }),
  projectUnread: 0,
  setUnread: (unread) => set({ unread }),
  setProjectUnread: (projectUnread) => set({ projectUnread }),
  setChatId: (chatId) => set({ chatId }),
  setOpenChatBox: (openChatBox) => set({ openChatBox }),
  setRoomId: (roomId) => set({ roomId }),
  setShow: (show) => set({ show }),
  setRoomInfo: (roomInfo) => set({ roomInfo }),
}));

const useFileRepoStore = create()(
  persist(
    (set) => ({
      files: {},
      metadatas: {},
      setFiles: (files) => set({ files }),
      setMetadatas: (metadatas) => set({ metadatas }),
      addFile: (key, file) =>
        set((state) => ({
          files: {
            ...state.files,
            [key]: file,
          },
        })),
      addMetadata: (key, metadata) =>
        set((state) => ({
          metadatas: {
            ...state.metadatas,
            [key]: metadata,
          },
        })),
      removeFile: (key) =>
        set((state) => {
          const { [key]: _, ...remainingFiles } = state.files;
          return { files: remainingFiles };
        }),
      removeMetadata: (key) =>
        set((state) => {
          const { [key]: _, ...remainingMetadatas } = state.metadatas;
          return { metadatas: remainingMetadatas };
        }),
      clear: () => set({ files: {}, metadatas: {} }),
    }),
    {
      name: "file-repository",
    }
  )
);

const chatPopupStore = create((set) => ({
  show: false,
  setShow: (show) => set({ show }),
}));

const fileStore = create((set) => ({
  activeKey: null,
  setActiveKey: (activeKey) => set({ activeKey }),
  files: {},
  previewFiles: {},
  // add files accepts a key and array of files
  addFiles: (key, files) =>
    set((state) => ({
      files: { ...state.files, [key]: files },
    })),
  addPreviewFiles: (key, files) =>
    set((state) => ({
      previewFiles: { ...state.previewFiles, [key]: files },
    })),
  getFiles: (key) => {
    const state = fileStore.getState();
    return state.files[key] || [];
  },
  getPreviewFiles: (key) => {
    const state = fileStore.getState();
    return state.previewFiles[key] || [];
  },
  deleteFile: (key, index) => {
    const files = fileStore.getState().files[key];
    set((state) => ({
      files: {
        ...state.files,
        [key]: Array.from(files).filter((_, i) => i !== index),
      },
    }));
  },
  setFiles: (files) => set({ files }),
  createPreviewUrl: (file) => {
    return new Promise((resolve, reject) => {
      if (!file || !(file instanceof File)) {
        reject(new Error("Invalid file object"));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  },
  getFileType: async (file) => {
    const obj = {
      link: null,
      mimeType: null,
      type: 'document',
    };
    if (file) {
      const fileBuffer = await file.arrayBuffer();
      const detectedType = await fileTypeFromBuffer(fileBuffer);

      const mimeType = detectedType ? detectedType.mime : file.type;

      obj.mimeType = mimeType;

      if (mimeType.startsWith("image/")) {
        obj.link = URL.createObjectURL(file);
        obj.type = 'image';
      } else if (mimeType.startsWith("video/")) {
        obj.link = URL.createObjectURL(file);
        obj.type = 'video';
      } else {
        obj.link = null;
        obj.type = 'document';
      }
    }
    return obj;
  },
  setPreviewFiles: (previewFiles) => set({ previewFiles }),
}));

const chatUserStore = create()(
  persist(
    (set) => ({
      firstName: null,
      setFirstName: (firstName) => set({ firstName }),
      lastName: null,
      setLastName: (lastName) => set({ lastName }),
      email: null,
      setEmail: (email) => set({ email }),
      message: null,
      setMessage: (message) => set({ message }),
      clear: () => set({ firstName: null, lastName: null, email: null, message: null }),
    }),
    {
      name: "chatUser",
    }
  )
);



const useBookingStore = create((set) => ({
  bookings: [],
  unapproved: [],
  changeOfTime: [],
  history: [],
  currentPage: 1,
  hasMore: true,
  isLoading: false,
  itemsPerPage: 10,
  setPage: (page) => set({ currentPage: page }),
  sortedBookings: { expired: [], upcoming: [], unapproved: [] },
  fetchBookings: async (status, page) => {
    try {
      set({ isLoading: true });
      makeRequest('GET', `/bookings?status=1&page=${page}`).then((response) => {
        const data = response.data.data;
        switch (status) {
          case 0:
            set((state) => ({
              bookings: [...state.bookings, ...data],
              hasMore: response.data.next_page_url !== null,
            }));
            break;

          default:
            break;
        }

      }).catch((error) => {
        Notify.failure("Failed to fetch bookings");
        console.error("Failed to fetch bookings", error);
      }).finally(() => {
        set({ isLoading: false });
      });
    } catch (error) {
      set({ isLoading: false });
      Notify.failure("Failed to fetch bookings");
      console.error("Failed to fetch bookings", error);
    }
  },
  loadMore: () => set((state) => ({ currentPage: state.currentPage + 1 })),
  setCurrentPage: (page) => set({ currentPage: page }),
}));



const analyticsStore = create((set) => ({
  data: {},
  setData: (data) => set({ data }),
  getAnalytics: async () => {
    makeRequest('GET', `${API_URL}/get-total-analytics`).then((response) => {
      set({ data: response.data.data });
    }).catch((error) => {
      Notify.failure("Failed to fetch analytics");
      console.error("Failed to fetch analytics", error);
    })
  }
}));

export const AppMeta = create((set) => ({
  roles: [
    {
      "id": 1,
      "name": "user"
    },
    {
      "id": 2,
      "name": "admin"
    }
  ],
  bookingStatus: {
    "PENDING_ADMIN_APPROVAL": "0",
    "APPROVED_BY_ADMIN": "1",
    "CLOSED_BY_ADMIN": "2",
    "CANCELLED_BY_ADMIN": "3",
    "REQUESTED_CHANGE_OF_TIME_BY_ADMIN": "4",
    "REQUESTED_CHANGE_OF_TIME_BY_USER": "5",
    "APPROVED_BY_USER": "6",
    "CANCELLED_BY_USER": "7"
  },
  projectStatus: {
    "PROJECT_CREATED_BY_ADMIN": '1',
    "PROJECT_IN_PROGRESS": '2',
    "PROJECT_CANCELLED": '3',
    "PROJECT_CLOSED_BY_ADMIN": '4',
    "CUSTOMER_APPROVE_CANCELLATION": "5",
    "PROJECT_CANCELLED_BY_CUSTOMER": '6'
  },
  chatTypes: {
    "TEXT": "1",
    "FILE": "2"
  },

  projectOrderStatus: {
    "PENDING": "0",
    "SUCCESS": "1",
    "FAILED": "2",
    "CANCELLED": "3",
    "REFUNDED": "4",
    "CUSTOMER_APPROVE_CANCELLATION": "5",
  },

  fileTypes: [
    {
      "id": 1,
      "name": "pdf",
      "status": "1"
    },
    {
      "id": 2,
      "name": "video",
      "status": "1"
    },
    {
      "id": 3,
      "name": "image",
      "status": "1"
    },
    {
      "id": 4,
      "name": "file",
      "status": "1"
    }
  ],
  setRoles: (roles) => {
    set({ roles: [...roles] })
  }

}))

// Define the initial state
const initialState = [
  { day: 'Monday', start: '09:00', end: '17:00' },
  { day: 'Tuesday', start: '09:00', end: '17:00' },
  { day: 'Wednesday', start: '09:00', end: '17:00' },
  { day: 'Thursday', start: '09:00', end: '17:00' },
  { day: 'Friday', start: '09:00', end: '17:00' },
  { day: 'Saturday', start: '10:00', end: '14:00' },
  { day: 'Sunday', start: '10:00', end: '14:00' },
];

// Create the Zustand store
const useAvailabilityStore = create((set) => ({
  availability: initialState,
  updateAvailability: (day, newStart, newEnd) =>
    set((state) => ({
      availability: state.availability.map((d) =>
        d.day === day ? { ...d, start: newStart, end: newEnd } : d
      ),
    })),
}));

const useTimeZoneStore = create((set) => ({
  timezones: [],
  getTzById: (id) => {
    let timezones = useTimeZoneStore.getState().timezones;
    return timezones.find(tz => tz.id === id);
  },
  fetchTimezones: async () => {
    makeRequest('GET', `${API_URL}/timezones`).then((response) => {
      set({ timezones: response.data.data });
    }).catch((error) => {
      console.error("Failed to fetch timezones", error);
      set({ timezones: timezonesFallback });
    })
  },
}));

const useBookingCardStore = create((set) => ({
  bookings: [],
  isLoading: false,
  setBookings: (bookings) => set({ bookings }),
  setIsLoading: (isLoading) => set({ isLoading }),
}))

const useBookingModal = create((set) => ({
  open: false,
  booking: {},
  id: null,
  setOpen: (open) => {
    set({ open })
  },
  setBooking: (booking) => set({ booking }),
  setId: (id) => set({ id }),
}))


const useChartStore = create((set) => ({
  projects: [],
  setProjects: (projects) => set({ projects }),
  chartData: [],
  setChartData: (chartData) => set({ chartData: [...chartData] }),
  startDate: null,
  endDate: null,
  setStartDate: (startDate) => set({ startDate }),
  setEndDate: (endDate) => set({ endDate }),
}))

export const useProjectCardStore = create((set) => ({
  projects: [],
  isLoading: false,
  fetchProjects: async () => {
    try {
      set({ isLoading: true });
      makeRequest('GET', `${API_URL}/projects?perPage=${5}&status=2`).then((response) => {
        set({ projects: response.data.data.data ?? [] });
      }).catch((error) => {
        console.log(error);
      }).finally(() => {
        set({ isLoading: false });
      })
    } catch (error) {
      console.log(error);
    }
  },
  setProjects: (projects) => set({ projects }),
  setIsLoading: (isLoading) => set({ isLoading }),
}))

const chatStore = create((set) => ({
  openChatBox: false,

  setOpenChatBox: (openChatBox) => set({ openChatBox }),

  offlineChats: [],

  setOfflineChats: (offlineChats) => set({ offlineChats }),

  roomId: null,

  roomInfo: null,
  setRoomInfo: (roomInfo) => set({ roomInfo }),

  setRoomId: (roomId) => set({ roomId }),
  isTyping: false,

  setIsTyping: (isTyping) => set({ isTyping }),
  messageType: null,

  setMessageType: (messageType) => set({ messageType }),
  typedMessage: "",

  setTypedMessage: (typedMessage) => set({ typedMessage }),

  chatId: "",

  setChatId: (chatId) => set({ chatId }),
}));

const useRoomStore = create()(
  persist(
    (set) => ({
      userRoomId: null,
      room: null,
      setRoom: (room) => set({ room }),
      setUserRoomId: (userRoomId) => set({ userRoomId }),
      clear: () => set({ room: null, userRoomId: null }),
    }),
    {
      name: "room-repository",
    }
  )
);

const usePresenceStore = create((set) => ({
  presence: {},
  setPresence: (presence) => set({ presence }),
}));

export { useProjectStore, useMeetingStore, userStore, navigationContext, usePortfolioStore, useProjectStatStore, appStore, chatState, useFileRepoStore, chatPopupStore, chatUserStore, fileStore, chatStore, useBookingStore, useAvailabilityStore, useTimeZoneStore, analyticsStore, useBookingCardStore, useBookingModal, useChartStore, useRoomStore, usePresenceStore };

